import api from '../api';
import { saveAs } from '../../utils/saveFile';
import {
  Contract,
  ContractBulkArchiveResult,
  ContractCreate,
  ContractDashboardResponse,
  ContractImportPreview,
  ContractImportResult,
  ContractNote,
  ContractStatus,
  ContractUpdate,
} from '../../types/compliance';

// NOTE: the shared axios instance (`services/api.ts`) is configured with
// baseURL = config.API_URL, which already includes the `/api/v1` prefix.
// Paths here are relative to that prefix.
const BASE = '/compliance/contracts';

export interface ContractListParams {
  search?: string;
  status?: ContractStatus;
  third_party_id?: string;
  owner_oid?: string;
  /** Case-insensitive substring match on category (Django parity). */
  category?: string;
  ending_before?: string; // YYYY-MM-DD
  ending_after?: string;  // YYYY-MM-DD
  /** contract_value >= this amount (stringified Decimal). */
  value_min?: string;
  /** contract_value <= this amount. */
  value_max?: string;
  include_deleted?: boolean;
  /** Archived contracts are left out unless this is true. */
  include_archived?: boolean;
}

export const contractsService = {
  async list(params?: ContractListParams): Promise<Contract[]> {
    const { data } = await api.get<Contract[]>(BASE, { params });
    return data;
  },

  async get(id: string): Promise<Contract> {
    const { data } = await api.get<Contract>(`${BASE}/${id}`);
    return data;
  },

  async create(body: ContractCreate): Promise<Contract> {
    const { data } = await api.post<Contract>(BASE, body);
    return data;
  },

  async update(id: string, body: ContractUpdate): Promise<Contract> {
    const { data } = await api.patch<Contract>(`${BASE}/${id}`, body);
    return data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  },

  async restore(id: string): Promise<Contract> {
    const { data } = await api.post<Contract>(`${BASE}/${id}/restore`);
    return data;
  },

  async archive(id: string): Promise<Contract> {
    const { data } = await api.post<Contract>(`${BASE}/${id}/archive`);
    return data;
  },

  async unarchive(id: string): Promise<Contract> {
    const { data } = await api.post<Contract>(`${BASE}/${id}/unarchive`);
    return data;
  },

  /** Archives the eligible contracts; the rest come back in `skipped`. */
  async bulkArchive(ids: string[]): Promise<ContractBulkArchiveResult> {
    const { data } = await api.post<ContractBulkArchiveResult>(`${BASE}/archive`, { ids });
    return data;
  },

  async exportCsv(): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    await saveAs(`contracts-${today}.csv`, async () => {
      const response = await api.get(`${BASE}/export.csv`, { responseType: 'blob' });
      return new Blob([response.data], { type: 'text/csv' });
    });
  },

  // --- Spreadsheet import ---------------------------------------------------

  async downloadImportTemplate(): Promise<void> {
    await saveAs('contracts-import-template.xlsx', async () => {
      const response = await api.get(`${BASE}/import-template`, { responseType: 'blob' });
      return new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  },

  /** Parses + validates the file server-side. Writes nothing. */
  async validateImport(file: File): Promise<ContractImportPreview> {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post<ContractImportPreview>(`${BASE}/import/validate`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /** All-or-nothing create. A 422 carries `detail.preview` with the row errors. */
  async importContracts(file: File): Promise<ContractImportResult> {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post<ContractImportResult>(`${BASE}/import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async getDashboard(department?: string): Promise<ContractDashboardResponse> {
    const params = department ? { department } : undefined;
    const { data } = await api.get<ContractDashboardResponse>(
      `${BASE}/dashboard`,
      { params },
    );
    return data;
  },

  async listNotes(id: string): Promise<ContractNote[]> {
    const { data } = await api.get<ContractNote[]>(`${BASE}/${id}/notes`);
    return data;
  },

  async addNote(id: string, body: string): Promise<ContractNote> {
    const { data } = await api.post<ContractNote>(`${BASE}/${id}/notes`, { body });
    return data;
  },
};

export default contractsService;
